import { useCallback, useEffect, useState } from "react";
import {
  APIError,
  listStorage,
  writeStorage,
  deleteStorage,
  readStorage,
  type StorageEntry,
  type StorageVolume,
} from "@/lib/api";
import { decodeBase64Text } from "@/lib/playbook";

// useFileTree manages a workspace volume's directory tree (playbook/skills/state):
// lazy per-folder listings, expand/collapse, and the file CRUD the FileTree pane
// drives. Rename/move are client-side (read→write→delete) for now.

export const PLAYBOOK_FILE = "PLAYBOOK.md";

export type TreeNode = { name: string; path: string; isDir: boolean; size: number };

export function joinPath(dir: string, name: string): string {
  return dir ? `${dir}/${name}` : name;
}

function toNodes(dir: string, entries: StorageEntry[]): TreeNode[] {
  const nodes: TreeNode[] = entries.map((e) => ({
    name: e.name,
    path: joinPath(dir, e.name),
    isDir: e.is_dir,
    size: e.size,
  }));
  nodes.sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    if (dir === "" && a.name === PLAYBOOK_FILE) return -1;
    if (dir === "" && b.name === PLAYBOOK_FILE) return 1;
    return a.name.localeCompare(b.name);
  });
  return nodes;
}

export function useFileTree(projectId: string, volume: StorageVolume) {
  const [byDir, setByDir] = useState<Record<string, TreeNode[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDir = useCallback(
    async (dir: string) => {
      try {
        const l = await listStorage(projectId, volume, dir);
        setByDir((m) => ({ ...m, [dir]: toNodes(dir, l.entries ?? []) }));
        setError(null);
      } catch (e) {
        if (e instanceof APIError && (e.status === 404 || e.status === 400)) {
          setByDir((m) => ({ ...m, [dir]: [] }));
          return;
        }
        setError("Couldn't load the files.");
      }
    },
    [projectId, volume],
  );

  // loadAll walks the whole volume breadth-first (bounded) and populates byDir
  // for every directory — quick-open needs the complete file list, but the tree
  // is otherwise lazily loaded per folder.
  const loadAll = useCallback(async () => {
    const acc: Record<string, TreeNode[]> = {};
    const queue: string[] = [""];
    const seen = new Set<string>();
    let budget = 2000;
    while (queue.length && budget > 0) {
      const dir = queue.shift() as string;
      if (seen.has(dir)) continue;
      seen.add(dir);
      try {
        const l = await listStorage(projectId, volume, dir);
        const nodes = toNodes(dir, l.entries ?? []);
        acc[dir] = nodes;
        budget -= nodes.length;
        for (const n of nodes) if (n.isDir) queue.push(n.path);
      } catch {
        acc[dir] = [];
      }
    }
    setByDir((m) => ({ ...m, ...acc }));
  }, [projectId, volume]);

  useEffect(() => {
    setLoading(true);
    setByDir({});
    setExpanded(new Set());
    void loadDir("").finally(() => setLoading(false));
  }, [loadDir]);

  const toggle = useCallback(
    (dir: string) => {
      setExpanded((s) => {
        const next = new Set(s);
        if (next.has(dir)) next.delete(dir);
        else {
          next.add(dir);
          if (byDir[dir] === undefined) void loadDir(dir);
        }
        return next;
      });
    },
    [byDir, loadDir],
  );

  const newFile = useCallback(
    async (dir: string, name: string) => {
      await writeStorage(projectId, volume, joinPath(dir, name), "");
      await loadDir(dir);
    },
    [projectId, volume, loadDir],
  );

  const remove = useCallback(
    async (path: string, dir: string) => {
      await deleteStorage(projectId, volume, path);
      await loadDir(dir);
    },
    [projectId, volume, loadDir],
  );

  // Client-side rename: read → write new → delete old.
  const rename = useCallback(
    async (path: string, dir: string, newName: string) => {
      const f = await readStorage(projectId, volume, path);
      const target = joinPath(dir, newName);
      await writeStorage(projectId, volume, target, decodeBase64Text(f.content_b64));
      await deleteStorage(projectId, volume, path);
      await loadDir(dir);
      return target;
    },
    [projectId, volume, loadDir],
  );

  // expand reveals a directory and every ancestor on its path (the tree only
  // renders a folder's children when each ancestor is expanded) — used when
  // navigating to a file so its folder is shown, however deeply nested.
  const expand = useCallback(
    (dir: string) => {
      if (!dir) return;
      const parts = dir.split("/");
      const prefixes: string[] = [];
      for (let i = 1; i <= parts.length; i++) prefixes.push(parts.slice(0, i).join("/"));
      setExpanded((s) => {
        const next = new Set(s);
        for (const p of prefixes) next.add(p);
        return next;
      });
      for (const p of prefixes) if (byDir[p] === undefined) void loadDir(p);
    },
    [byDir, loadDir],
  );

  // pathExists authoritatively reports whether a file already lives at path by
  // listing its parent directory fresh — so a create/rename can refuse to
  // overwrite even a file in a folder the tree hasn't lazily loaded yet (an
  // in-memory byDir check would miss collapsed dirs and silently overwrite).
  const pathExists = useCallback(
    async (path: string) => {
      const i = path.lastIndexOf("/");
      const dir = i === -1 ? "" : path.slice(0, i);
      const name = i === -1 ? path : path.slice(i + 1);
      try {
        const l = await listStorage(projectId, volume, dir);
        return (l.entries ?? []).some((e) => e.name === name);
      } catch {
        return false;
      }
    },
    [projectId, volume],
  );

  return { byDir, expanded, loading, error, loadDir, loadAll, toggle, expand, pathExists, newFile, remove, rename };
}
